import type { NetplayInput, NetplayPlayer, NetplayState, JsonValue } from 'netplayjs';

type InputEntry<TInput extends NetplayInput<TInput>> = {
  input: TInput;
  isPrediction: boolean;
};

interface DesyncDetails {
  frame: number;
  localHash: string;
  remoteHash: string;
}

export interface DesyncInfo {
  reason: 'hash_mismatch' | 'input_order_violation' | 'missing_rollback_anchor' | 'remote_desync';
  details?: DesyncDetails;
}

type SendInputFn<TInput extends NetplayInput<TInput>> = (frame: number, input: TInput) => void;
type SendStateHashFn = (frame: number, hash: string) => void;
type DesyncFn = (info: DesyncInfo) => void;

interface HistoryEntry<TInput extends NetplayInput<TInput>> {
  frame: number;
  state: JsonValue;
  inputs: Map<NetplayPlayer, InputEntry<TInput>>;
  localHash: string | null;
  hashCompared: boolean;
}

function mapGet<K, V>(map: Map<K, V>, key: K): V {
  const value = map.get(key);
  if (value !== undefined) return value;
  throw new Error(`Missing key ${String(key)}`);
}

function cloneJson<T extends JsonValue>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function shift<T>(array: T[]): T {
  const value = array.shift();
  if (value !== undefined) return value;
  throw new Error('Cannot shift empty array');
}

function fnv1a32(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function hashState(serialized: JsonValue): string {
  return fnv1a32(JSON.stringify(serialized));
}

// Deterministic peer rollback:
// - advance every frame with local input + remote input/prediction
// - when late remote input arrives, rewind to pre-prediction frame and replay
// - compare per-frame hashes once all inputs are authoritative
export class PeerRollbackNetcode<
  TState extends NetplayState<TInput>,
  TInput extends NetplayInput<TInput>
> {
  readonly history: HistoryEntry<TInput>[];
  readonly maxPredictedFrames: number;
  readonly future: Map<NetplayPlayer, Array<{ frame: number; input: TInput }>>;
  readonly highestFrameReceived: Map<NetplayPlayer, number>;

  private readonly state: TState;
  private readonly localPlayer: NetplayPlayer;
  private readonly pollInput: () => TInput;
  private readonly sendInput: SendInputFn<TInput>;
  private readonly sendStateHash: SendStateHashFn;
  private readonly onDesync: DesyncFn;
  private readonly remoteHashes: Map<number, string>;
  private readonly timestep: number;

  private tickInterval: ReturnType<typeof setInterval> | null;
  private desyncTriggered: boolean;

  constructor(
    initialState: TState,
    players: NetplayPlayer[],
    initialInputs: Map<NetplayPlayer, TInput>,
    maxPredictedFrames: number,
    timestep: number,
    pollInput: () => TInput,
    sendInput: SendInputFn<TInput>,
    sendStateHash: SendStateHashFn,
    onDesync: DesyncFn
  ) {
    const localPlayers = players.filter((player) => player.isLocalPlayer());
    if (localPlayers.length !== 1) {
      throw new Error(`Expected exactly one local player, got ${localPlayers.length}`);
    }

    this.state = initialState;
    this.localPlayer = localPlayers[0];
    this.maxPredictedFrames = maxPredictedFrames;
    this.pollInput = pollInput;
    this.sendInput = sendInput;
    this.sendStateHash = sendStateHash;
    this.onDesync = onDesync;
    this.timestep = timestep;
    this.tickInterval = null;
    this.desyncTriggered = false;

    const historyInputs = new Map<NetplayPlayer, InputEntry<TInput>>();
    for (const [player, input] of initialInputs.entries()) {
      historyInputs.set(player, { input, isPrediction: false });
    }
    this.history = [
      {
        frame: 0,
        state: cloneJson(this.state.serialize()),
        inputs: historyInputs,
        localHash: null,
        hashCompared: false,
      },
    ];

    this.future = new Map();
    this.highestFrameReceived = new Map();
    for (const player of players) {
      this.future.set(player, []);
      this.highestFrameReceived.set(player, 0);
    }

    this.remoteHashes = new Map();
    this.processHashesAndCleanup();
  }

  currentFrame(): number {
    return this.history[this.history.length - 1].frame;
  }

  largestFutureSize(): number {
    return Math.max(...Array.from(this.future.values()).map((items) => items.length));
  }

  predictedFrames(): number {
    for (let i = 0; i < this.history.length; i++) {
      if (!this.allInputsSynced(this.history[i])) {
        return this.history.length - i;
      }
    }
    return 0;
  }

  shouldStall(): boolean {
    return this.predictedFrames() > this.maxPredictedFrames;
  }

  isDesynced(): boolean {
    return this.desyncTriggered;
  }

  onRemoteInput(frame: number, player: NetplayPlayer, input: TInput): void {
    if (this.desyncTriggered) return;

    const expectedFrame = mapGet(this.highestFrameReceived, player) + 1;
    if (frame !== expectedFrame) {
      this.triggerDesync({ reason: 'input_order_violation' });
      return;
    }
    this.highestFrameReceived.set(player, expectedFrame);

    const newestFrame = this.history[this.history.length - 1].frame;
    if (frame > newestFrame) {
      mapGet(this.future, player).push({ frame, input });
      return;
    }

    const firstPrediction = this.history.findIndex((entry) => {
      if (entry.frame < frame) return false;
      const playerInput = entry.inputs.get(player);
      return Boolean(playerInput?.isPrediction);
    });

    if (firstPrediction < 0) {
      // Duplicate/late input for an already resolved frame; safe to ignore.
      return;
    }

    // Rewind to the last fully known state before predicted frames.
    const rollbackAnchor = this.history[firstPrediction - 1];
    if (!rollbackAnchor) {
      this.triggerDesync({ reason: 'missing_rollback_anchor' });
      return;
    }

    this.state.deserialize(rollbackAnchor.state);

    for (let i = firstPrediction; i < this.history.length; i++) {
      const current = this.history[i];
      const currentPlayerInput = mapGet(current.inputs, player);

      if (i === firstPrediction) {
        currentPlayerInput.isPrediction = false;
        currentPlayerInput.input = input;
      } else {
        const previous = this.history[i - 1];
        const previousPlayerInput = mapGet(previous.inputs, player);
        // Netplay prediction contract: hold previous frame's button state.
        currentPlayerInput.input = previousPlayerInput.input.predictNext();
      }

      this.state.tick(this.stripPredictionFlags(current.inputs));
      current.state = cloneJson(this.state.serialize());
      current.localHash = null;
      current.hashCompared = false;
    }

    this.processHashesAndCleanup();
  }

  onRemoteStateHash(frame: number, hash: string): void {
    if (this.desyncTriggered) return;
    this.remoteHashes.set(frame, hash);
    this.processHashesAndCleanup();
  }

  onRemoteDesync(): void {
    this.triggerDesync({ reason: 'remote_desync' });
  }

  tick(): void {
    if (this.desyncTriggered) return;
    if (this.shouldStall()) return;

    const lastState = this.history[this.history.length - 1];
    const newInputs = new Map<NetplayPlayer, InputEntry<TInput>>();

    for (const [player, previousInput] of lastState.inputs.entries()) {
      if (player === this.localPlayer) {
        const localInput = this.pollInput();
        newInputs.set(player, { input: localInput, isPrediction: false });
        this.sendInput(lastState.frame + 1, localInput);
        continue;
      }

      const queued = mapGet(this.future, player);
      if (queued.length > 0) {
        const nextQueued = shift(queued);
        if (nextQueued.frame !== lastState.frame + 1) {
          this.triggerDesync({ reason: 'input_order_violation' });
          return;
        }
        newInputs.set(player, { input: nextQueued.input, isPrediction: false });
      } else {
        newInputs.set(player, {
          input: previousInput.input.predictNext(),
          isPrediction: true,
        });
      }
    }

    this.state.tick(this.stripPredictionFlags(newInputs));
    this.history.push({
      frame: lastState.frame + 1,
      state: cloneJson(this.state.serialize()),
      inputs: newInputs,
      localHash: null,
      hashCompared: false,
    });

    this.processHashesAndCleanup();
  }

  start(): void {
    if (this.tickInterval !== null) return;
    this.tickInterval = setInterval(() => {
      let ticksToRun = 1;
      if (this.largestFutureSize() > 0) {
        ticksToRun = 2;
      }
      for (let i = 0; i < ticksToRun; i++) {
        this.tick();
      }
    }, this.timestep);
  }

  stop(): void {
    if (this.tickInterval !== null) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }

  private stripPredictionFlags(
    inputs: Map<NetplayPlayer, InputEntry<TInput>>
  ): Map<NetplayPlayer, TInput> {
    const stateInputs = new Map<NetplayPlayer, TInput>();
    for (const [player, { input }] of inputs.entries()) {
      stateInputs.set(player, input);
    }
    return stateInputs;
  }

  private allInputsSynced(entry: HistoryEntry<TInput>): boolean {
    for (const { isPrediction } of entry.inputs.values()) {
      if (isPrediction) return false;
    }
    return true;
  }

  private processHashesAndCleanup(): void {
    if (this.desyncTriggered) return;

    for (const entry of this.history) {
      if (!this.allInputsSynced(entry)) continue;

      if (entry.localHash === null) {
        entry.localHash = hashState(entry.state);
        this.sendStateHash(entry.frame, entry.localHash);
      }

      const remoteHash = this.remoteHashes.get(entry.frame);
      if (!remoteHash || entry.hashCompared === true) continue;

      if (entry.localHash !== remoteHash) {
        this.triggerDesync({
          reason: 'hash_mismatch',
          details: {
            frame: entry.frame,
            localHash: entry.localHash,
            remoteHash,
          },
        });
        return;
      }

      entry.hashCompared = true;
      this.remoteHashes.delete(entry.frame);
    }

    // Keep enough history for future rollbacks, but release frames once both
    // peers have compared hashes and the next frame is fully authoritative.
    while (this.history.length >= 2) {
      const first = this.history[0];
      const second = this.history[1];
      if (first.hashCompared && this.allInputsSynced(second)) {
        shift(this.history);
      } else {
        break;
      }
    }
  }

  private triggerDesync(info: DesyncInfo): void {
    if (this.desyncTriggered) return;
    this.desyncTriggered = true;
    this.stop();
    this.onDesync(info);
  }
}
